import * as ACT from './actions'
import { createReducer } from 'redux-create-reducer'
import update from 'immutability-helper'
import cloneDeep from 'lodash/cloneDeep'
import moment from 'moment'

const resetSelectedCardsFromDifferentLanes = (state, laneId, selectedLaneId) => {
  const newState = cloneDeep(state)

  if (selectedLaneId && selectedLaneId !== laneId) {
    for (const card of newState.rowData[selectedLaneId]) {
      card.selected = false
    }
  }

  return newState
}

const resetSelectedCardsFromLane = (state, laneId, selected = false) => {
  const newState = cloneDeep(state)

  for (const card of newState.rowData[laneId]) {
    card.selected = selected
  }

  return newState
}

const selectAllCardsFromLane = (state, laneId) => {
  return resetSelectedCardsFromLane(state, laneId, true)
}

const initialState = {
  rowData: [],
  pagination: {},
  dealsHeader: {},
  dealsStatuses: [],
  organizationStatuses: []
}

const dealsReducer = createReducer(initialState, {
  [ACT.DEAL_STATUSES_FETCH_SUCCESS](state, action) {
    const { statuses } = action.payload
    return {
      ...state,
      dealsStatuses: statuses
    }
  },
  [ACT.ORGANIZATION_STATUSES_FETCH_SUCCESS](state, action) {
    const { statuses } = action.payload
    return {
      ...state,
      organizationStatuses: statuses
    }
  },
  [ACT.DEALS_ROW_DATA_FETCH_SUCCESS](state, action) {
    const { rowData } = action.payload
    return {
      ...state,
      rowData
    }
  },
  [ACT.DEAL_DATE_UPDATE_SUCCESS](state, action) {
    const { deal } = action.payload
    const index = state.rowData.findIndex(d => +d.id === +deal.id)

    return update(state, {
      rowData: {
        [index]: { $set: deal }
      }
    })
  },
  [ACT.DEALS_BOARD_ROW_DATA_FETCH_SUCCESS](state, action) {
    const { rowData } = action.payload
    return {
      ...state,
      rowData,
      pagination: initialState.pagination
    }
  },
  [ACT.CREATE_DEAL_SUCCESS](state, action) {
    const { deal, fromDashboard } = action.payload

    if (fromDashboard) {
      return state
    }

    return update(state, {
      rowData: {
        [deal.status.code]: { $push: [deal] }
      }
    })
  },
  [ACT.DEAL_UPDATE_STATUS_SUCCESS](state, action) {
    const { willMove, oldStatus, status, id, bidStatus, wonOn, lostOn } = action.payload
    const boardIndex = state.rowData[oldStatus].findIndex(d => +d.id === +id)
    const deal = state.rowData[oldStatus][boardIndex]

    if (willMove && oldStatus !== status) {
      deal.status = status
      deal.selected = false
      if (bidStatus) {
        deal.bidStatus = bidStatus
      }
      if (wonOn) {
        deal.wonDate = wonOn
      }
      if (lostOn) {
        deal.lostDate = lostOn
      }
      return update(state, {
        rowData: {
          [oldStatus]: { $splice: [[boardIndex, 1]] },
          [status]: { $unshift: [deal] }
        }
      })
    }

    return update(state, {
      rowData: {
        [oldStatus]: {
          [boardIndex]: { $set: deal }
        }
      }
    })
  },
  [ACT.DEAL_FOLLOWUP_DATE_UPDATE_SUCCESS](state, action) {
    const { deal, oldStatus, awardedCompany } = action.payload
    const { status, followUpDate } = deal
    const property = oldStatus || status.code || status.value
    const index = state.rowData[property].findIndex(d => +d.id === +deal.id)
    const dealProposals = deal.proposals?.map(proposal => ({ ...proposal, followUpDate })) ?? []

    const newState = update(state, {
      rowData: {
        [property]: {
          [index]: {
            followUpDate: { $set: followUpDate },
            proposals: { $set: dealProposals },
            awardedCompany: { $set: awardedCompany || deal.awardedCompany }
          }
        }
      }
    })

    const sortedColumn = newState.rowData[property].sort((deal1, deal2) =>
      moment(deal1.followUpDate).diff(moment(deal2.followUpDate))
    )

    return update(newState, {
      rowData: {
        [property]: { $set: sortedColumn }
      }
    })
  },
  [ACT.DEAL_REVERT_STATUS](state, action) {
    const { deal } = action.payload
    const { status } = deal
    const property = status.code || status
    const index = state.rowData[status.code || status].findIndex(d => +d.id === +deal.id)

    return update(state, {
      rowData: {
        [property]: {
          [index]: { $set: cloneDeep(deal) }
        }
      }
    })
  },
  [ACT.DEAL_BOARD_TOGGLE_SELECT_DEAL](state, action) {
    const { laneId, dealId, selectedLaneId } = action.payload
    const boardIndex = state.rowData[laneId].findIndex(d => +d.id === +dealId)
    const selected = state.rowData[laneId][boardIndex].selected

    const newState = resetSelectedCardsFromDifferentLanes(state, laneId, selectedLaneId)

    return update(newState, {
      rowData: {
        [laneId]: {
          [boardIndex]: {
            selected: { $set: !selected }
          }
        }
      }
    })
  },
  [ACT.DEAL_BOARD_TOGGLE_SELECT_ALL_DEALS](state, action) {
    const { laneId, selected, selectedLaneId } = action.payload

    const newState = resetSelectedCardsFromDifferentLanes(state, laneId, selectedLaneId)
    return !selected
      ? selectAllCardsFromLane(newState, laneId)
      : resetSelectedCardsFromLane(newState, laneId)
  },
  [ACT.DEAL_BOARD_EXPAND_DEAL](state, action) {
    const { laneId, cardId } = action.payload

    const newState = cloneDeep(state)

    for (const card of newState.rowData[laneId]) {
      if (card.id === cardId) {
        card.expanded = !card.expanded
      }
    }

    return newState
  },
  [ACT.DEALS_BOARD_ADD_DEALS_TO_COLUMN](state, action) {
    const { deals, last, laneId, page } = action.payload
    const pagination = cloneDeep(state.pagination)

    pagination[laneId] = {
      page,
      isLast: last
    }
    return update(state, {
      rowData: {
        [laneId]: { $push: [...deals] }
      },
      pagination: { $set: pagination }
    })
  },
  [ACT.DEALS_BOARD_ADD_DEALS_HEADER_TO_COLUMNS](state, action) {
    const { dealsHeader } = action.payload
    return {
      ...state,
      dealsHeader
    }
  },
  [ACT.DEALS_FOLLOWUP_DATE_UPDATE_SUCCESS](state, action) {
    const { deals, status } = action.payload
    const pagination = cloneDeep(state.pagination)
    if (pagination[status]) {
      delete pagination[status]
    }

    return update(state, {
      rowData: {
        [status]: { $set: deals }
      },
      pagination: { $set: pagination }
    })
  },
  [ACT.DEALS_BOARD_FETCH_DEAL_TASKS_SUCCESS](state, action) {
    const { laneId, cardId, tasks } = action.payload
    const newState = cloneDeep(state)

    for (let card of newState.rowData[laneId]) {
      if (card.id === cardId) {
        card.completedTasks = tasks.completedTasks
        card.firstTaskDueDate = tasks.firstTaskDueDate
        card.futureTasks = tasks.futureTasks
        card.lateTasks = tasks.lateTasks
        card.notScheduledTasks = tasks.notScheduledTasks
        card.todayTasks = tasks.todayTasks
      }
    }

    return newState
  }
})

export default dealsReducer
